/**
 * React Query key factory — core / settings keys (users, entities, roles,
 * permissions, tenantConfig, profile, help, auditTrail, sentEmails,
 * documentTemplates, emailTemplates, ged, entityBranding, titles, apiKeys, tags).
 *
 * Référence centrale dans `queryKeys.ts` (barrel).
 */

const usersAll = ['users'] as const
const entitiesAll = ['entities'] as const
const rolesAll = ['roles'] as const
const auditTrailAll = ['auditTrail'] as const
const sentEmailsAll = ['sentEmails'] as const
const documentTemplatesAll = ['documentTemplates'] as const
const emailTemplatesAll = ['emailTemplates'] as const
const gedAll = ['ged'] as const
const exportsAll = ['exports'] as const
const titlesAll = ['titles'] as const
const apiKeysAll = ['apiKeys'] as const
const tagsAll = ['tags'] as const

export const coreKeys = {
    users: {
        all: usersAll,
        list: (filters?: Record<string, string>) => [...usersAll, 'list', filters] as const,
        detail: (id: string) => [...usersAll, 'detail', id] as const,
    },
    entities: {
        all: entitiesAll,
        tree: () => [...entitiesAll, 'tree'] as const,
        // ADMIN-013 — Paramètres PA (mode d'émission RFE) par entité.
        paSettings: (entityId: string) => [...entitiesAll, 'pa-settings', entityId] as const,
    },
    roles: {
        all: rolesAll,
        list: () => [...rolesAll, 'list'] as const,
        detail: (id: string) => [...rolesAll, 'detail', id] as const,
    },
    permissions: {
        all: ['permissions'] as const,
    },
    tenantConfig: {
        all: ['tenantConfig'] as const,
    },
    profile: {
        all: ['profile'] as const,
    },
    notificationPreferences: {
        all: ['notificationPreferences'] as const,
    },
    help: {
        articles: (contexts: string[], lang: string) => ['help', 'articles', contexts, lang] as const,
        // `scope` distingue anonyme vs utilisateur : l'API restreint l'index aux
        // articles publics pour un visiteur non authentifié — ne pas mutualiser le
        // cache entre une page publique et l'app connectée.
        searchIndex: (lang: string, scope: string) =>
            ['help', 'search-index', lang, scope] as const,
    },
    auditTrail: {
        all: auditTrailAll,
        entries: (filters: Record<string, string>) => [...auditTrailAll, 'entries', filters] as const,
        users: () => [...auditTrailAll, 'users'] as const,
    },
    sentEmails: {
        all: sentEmailsAll,
        entries: (filters: Record<string, string>) => [...sentEmailsAll, 'entries', filters] as const,
        stats: (filters: Record<string, string>) => [...sentEmailsAll, 'stats', filters] as const,
        senders: () => [...sentEmailsAll, 'senders'] as const,
        types: () => [...sentEmailsAll, 'types'] as const,
    },
    documentTemplates: {
        all: documentTemplatesAll,
        list: (params?: string) => [...documentTemplatesAll, 'list', params] as const,
        detail: (id: string) => [...documentTemplatesAll, 'detail', id] as const,
        versions: (id: string) => [...documentTemplatesAll, 'versions', id] as const,
    },
    emailTemplates: {
        all: emailTemplatesAll,
        list: (params?: string) => [...emailTemplatesAll, 'list', params] as const,
        detail: (id: string) => [...emailTemplatesAll, 'detail', id] as const,
        // CUSTOM-014 (L8) — la lecture propre à l'éditeur, une par ouverture, qu'aucun envoi n'observe
        // (`components/email-editor/useLectureDeLEditeur.ts`).
        editeur: (id: string, ouverture: number) => [...emailTemplatesAll, 'editeur', id, ouverture] as const,
    },
    ged: {
        all: gedAll,
        folders: (entityId: string) => [...gedAll, 'folders', entityId] as const,
        // DOC-003bis Phase 3 — enfants virtuels « Documents rattachés » (lazy par token).
        virtualFolders: (entityId: string, parent?: string | null) =>
            [...gedAll, 'virtual-folders', entityId, parent ?? 'root'] as const,
        // `filters` = objet de filtres quelconque (FileListFilters : limit:number,
        // show_all:boolean…) ; TanStack hache la clé par VALEUR structurelle, le type
        // exact importe peu → `object` évite un cast au call-site (pas de Record<string,string>).
        files: (folderId?: string | null, filters?: object) =>
            [...gedAll, 'files', folderId, filters] as const,
        fileDetail: (id: string) => [...gedAll, 'file', id] as const,
        quota: (entityId: string) => [...gedAll, 'quota', entityId] as const,
        // DOC-003bis Phase 6 — règles d'auto-attache portées par un fichier.
        attachRules: (entityId: string, fileId: string) =>
            [...gedAll, 'attach-rules', entityId, fileId] as const,
        attachSummary: (entityId: string, fileIds: string[]) =>
            [...gedAll, 'attach-summary', entityId, fileIds] as const,
    },
    // DOC-003bis Phase 5 — centre d'export transverse (« Exports »).
    exports: {
        all: exportsAll,
        list: () => [...exportsAll, 'list'] as const,
        detail: (id: string) => [...exportsAll, 'detail', id] as const,
        unseenCount: () => [...exportsAll, 'unseen-count'] as const,
    },
    entityBranding: {
        all: ['entity-branding'] as const,
        detail: (entityId: string) => ['entity-branding', entityId] as const,
    },
    titles: {
        all: titlesAll,
        list: (search?: string) => [...titlesAll, 'list', search] as const,
    },
    apiKeys: {
        all: apiKeysAll,
        list: () => [...apiKeysAll, 'list'] as const,
        detail: (id: string) => [...apiKeysAll, 'detail', id] as const,
        usage: () => [...apiKeysAll, 'usage'] as const,
        keyUsage: (id: string) => [...apiKeysAll, 'keyUsage', id] as const,
        permissionOptions: () => [...apiKeysAll, 'permissionOptions'] as const,
    },
    tags: {
        all: tagsAll,
        byScope: (entityId: string, scope: string) =>
            [...tagsAll, 'byScope', entityId, scope] as const,
        entityTags: (targetType: string, targetId: string) =>
            [...tagsAll, 'entityTags', targetType, targetId] as const,
    },
}
